import {
	ExperiencePerformanceTypes,
	ExperienceTypes,
	payloadPublisher,
	UFOExperience,
} from '@atlassian/ufo';
import { client } from './analytics-client';

export const panelLoadExperience: UFOExperience = new UFOExperience('csm-ai-widget-iframe-load', {
	type: ExperienceTypes.Load,
	performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
});

export const widgetOpenToOnlineExperience: UFOExperience = new UFOExperience(
	'csm-ai-widget-open-to-online',
	{
		type: ExperienceTypes.Load,
		performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
	},
);

export enum WidgetLoadExperienceSegments {
	/**
	 * The time it takes for the entrypoint script to load
	 */
	SCRIPT_LOAD = 'script-load',
	/**
	 * The time from when the script has loaded until the SDK is initialized
	 */
	SDK_INIT = 'sdk-init',
	/**
	 * The time it takes to fetch the widget configuration from the server
	 */
	CONFIG_FETCH = 'config-fetch',
	/**
	 * The time it takes for the customer authenticate callback to complete
	 */
	AUTHENTICATE = 'authenticate',
	/**
	 * The time it takes to perform the access check
	 */
	ACCESS_CHECK = 'access-check',
	/**
	 * The time from when the script has loaded the SDK until the widget has initialized
	 * (rendered the bubble in the DOM, iframe not yet loaded)
	 */
	WIDGET_INIT = 'widget-init',
	/**
	 * The time to load the iframe from when the iframe HTML starts to load
	 * until the iframe has loaded its HTML
	 */
	IFRAME_HTML_LOAD = 'iframe-html-load',
	/**
	 * The time to load the iframe from when the iframe HTML finishes loading
	 * until the iframe has loaded the JS and has evaluated it
	 */
	IFRAME_JS_LOAD = 'iframe-js-load',
	/**
	 * The time from when the iframe HTML has loaded until the parent host
	 * receives the init message
	 */
	IFRAME_INIT = 'iframe-init',
	/**
	 * The time from when the component goes from launching to online
	 */
	COMPONENT_LAUNCH = 'component-launch',
}

export enum WidgetLoadExperienceMarks {
	SCRIPT_LOAD_START = 'script-load-start',
	SCRIPT_LOAD_END = 'script-load-end',
	SDK_INIT = 'sdk-init',
	CONFIG_FETCH_START = 'config-fetch-start',
	CONFIG_FETCH_END = 'config-fetch-end',
	AUTHENTICATE_START = 'authenticate-start',
	AUTHENTICATE_END = 'authenticate-end',
	ACCESS_CHECK_START = 'access-check-start',
	ACCESS_CHECK_END = 'access-check-end',
	WIDGET_INIT = 'widget-init',
	IFRAME_HTML_LOAD_START = 'iframe-html-load-start',
	IFRAME_HTML_LOAD_END = 'iframe-html-load-end',
	IFRAME_LOAD_END = 'iframe-load-end',
	COMPONENT_LAUNCHING = 'component-launching',
	COMPONENT_ONLINE = 'component-online',
}

export const widgetLoadExperience: UFOExperience = new UFOExperience('csm-ai-widget-load', {
	type: ExperienceTypes.Load,
	performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
	timings: [
		{
			key: WidgetLoadExperienceSegments.SCRIPT_LOAD,
			startMark: WidgetLoadExperienceMarks.SCRIPT_LOAD_START,
			endMark: WidgetLoadExperienceMarks.SCRIPT_LOAD_END,
		},
		{
			key: WidgetLoadExperienceSegments.SDK_INIT,
			startMark: WidgetLoadExperienceMarks.SCRIPT_LOAD_END,
			endMark: WidgetLoadExperienceMarks.SDK_INIT,
		},
		{
			key: WidgetLoadExperienceSegments.CONFIG_FETCH,
			startMark: WidgetLoadExperienceMarks.CONFIG_FETCH_START,
			endMark: WidgetLoadExperienceMarks.CONFIG_FETCH_END,
		},
		{
			key: WidgetLoadExperienceSegments.AUTHENTICATE,
			startMark: WidgetLoadExperienceMarks.AUTHENTICATE_START,
			endMark: WidgetLoadExperienceMarks.AUTHENTICATE_END,
		},
		{
			key: WidgetLoadExperienceSegments.ACCESS_CHECK,
			startMark: WidgetLoadExperienceMarks.ACCESS_CHECK_START,
			endMark: WidgetLoadExperienceMarks.ACCESS_CHECK_END,
		},
		{
			key: WidgetLoadExperienceSegments.WIDGET_INIT,
			startMark: WidgetLoadExperienceMarks.SDK_INIT,
			endMark: WidgetLoadExperienceMarks.WIDGET_INIT,
		},
		{
			key: WidgetLoadExperienceSegments.IFRAME_HTML_LOAD,
			startMark: WidgetLoadExperienceMarks.IFRAME_HTML_LOAD_START,
			endMark: WidgetLoadExperienceMarks.IFRAME_HTML_LOAD_END,
		},
		{
			key: WidgetLoadExperienceSegments.IFRAME_JS_LOAD,
			startMark: WidgetLoadExperienceMarks.IFRAME_HTML_LOAD_END,
			endMark: WidgetLoadExperienceMarks.IFRAME_LOAD_END,
		},
		{
			key: WidgetLoadExperienceSegments.COMPONENT_LAUNCH,
			startMark: WidgetLoadExperienceMarks.COMPONENT_LAUNCHING,
			endMark: WidgetLoadExperienceMarks.COMPONENT_ONLINE,
		},
	],
});

export const markWidgetLoadStart = (metrics: {
	scriptLoadStartTime?: number;
	scriptLoadEndTime?: number;
	sdkInitTime: number;
	instanceInitTime: number;
	iframeInitTime: number;
	iframeHTMLLoadStartTime?: number;
	iframeHTMLLoadEndTime?: number;
	iframeLoadEndTime: number;
	configFetchStartTime: number;
	configFetchEndTime: number;
	authenticateStartTime: number;
	authenticateEndTime: number;
	accessCheckStartTime: number;
	accessCheckEndTime: number;
}): void => {
	const originOffset = -1 * (metrics.iframeInitTime - (metrics.scriptLoadStartTime ?? 0));
	widgetLoadExperience.start(originOffset);

	/**
	 * Computes a negative mark for the events that happened before the timeorigin in the iframe
	 */
	const getHostSiteMark = (hostMetricTimestamp: number | undefined) =>
		originOffset + (hostMetricTimestamp ?? 0) - (metrics?.scriptLoadStartTime ?? 0);

	widgetLoadExperience.mark(
		WidgetLoadExperienceMarks.SCRIPT_LOAD_START,
		getHostSiteMark(metrics.scriptLoadStartTime),
	);
	widgetLoadExperience.mark(
		WidgetLoadExperienceMarks.SCRIPT_LOAD_END,
		getHostSiteMark(metrics.scriptLoadEndTime),
	);
	widgetLoadExperience.mark(
		WidgetLoadExperienceMarks.SDK_INIT,
		getHostSiteMark(metrics.sdkInitTime),
	);
	widgetLoadExperience.mark(
		WidgetLoadExperienceMarks.CONFIG_FETCH_START,
		getHostSiteMark(metrics.configFetchStartTime),
	);
	widgetLoadExperience.mark(
		WidgetLoadExperienceMarks.CONFIG_FETCH_END,
		getHostSiteMark(metrics.configFetchEndTime),
	);
	widgetLoadExperience.mark(
		WidgetLoadExperienceMarks.AUTHENTICATE_START,
		getHostSiteMark(metrics.authenticateStartTime),
	);
	widgetLoadExperience.mark(
		WidgetLoadExperienceMarks.AUTHENTICATE_END,
		getHostSiteMark(metrics.authenticateEndTime),
	);
	widgetLoadExperience.mark(
		WidgetLoadExperienceMarks.ACCESS_CHECK_START,
		getHostSiteMark(metrics.accessCheckStartTime),
	);
	widgetLoadExperience.mark(
		WidgetLoadExperienceMarks.ACCESS_CHECK_END,
		getHostSiteMark(metrics.accessCheckEndTime),
	);
	widgetLoadExperience.mark(
		WidgetLoadExperienceMarks.WIDGET_INIT,
		getHostSiteMark(metrics.instanceInitTime),
	);
	/**
	 * We will mark the FMP as the instance init time, because that's when the widget UI is first
	 * visible to the user.
	 */
	widgetLoadExperience.markFMP(getHostSiteMark(metrics.instanceInitTime));
	widgetLoadExperience.mark(
		WidgetLoadExperienceMarks.IFRAME_HTML_LOAD_START,
		getHostSiteMark(metrics.iframeHTMLLoadStartTime),
	);
	widgetLoadExperience.mark(
		WidgetLoadExperienceMarks.IFRAME_HTML_LOAD_END,
		getHostSiteMark(metrics.iframeHTMLLoadEndTime),
	);
	widgetLoadExperience.mark(WidgetLoadExperienceMarks.IFRAME_LOAD_END, metrics.iframeLoadEndTime);
};

export const initUFOV1 = (): void => {
	payloadPublisher.setup({
		gasv3: client,
		product: 'csm',
		app: {
			version: { web: process.env.PACKAGE_VERSION ?? 'unknown' },
		},
	});
};
